import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ROUTER_NAVIGATED } from '@ngrx/router-store';
import { ToastrService } from 'ngx-toastr';
import { of } from 'rxjs';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';

import { DashboardCamelCasePipe } from 'src/app/core/pipes/dashboard-camel-case/dashboard-camel-case.pipe';
import { DashboardSnakeCasePipe } from 'src/app/core/pipes/dashboard-snake-case/dashboard-snake-case.pipe';
import { CustomRouterNavigatedAction } from 'src/app/core/custom-route-serializer';
import { STUDIES_ROUTES } from '../../studies/studies.routes';
import * as calculatedVariableActions from './calculated-variable.actions';
import { CalculatedVariablesService } from '../../services/calculated-variables/calculated-variables.service';
import { CalculatedVariable } from 'src/app/models/calculated-variable';

@Injectable()
export class CalculatedVariableEffects {
  calculatedVariablesRoute$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CustomRouterNavigatedAction>(ROUTER_NAVIGATED),
      filter((route: CustomRouterNavigatedAction) => {
        return route.payload.routerState.url.endsWith(`/${STUDIES_ROUTES.CALCULATED_VARIABLES}`);
      }),
      map((action) =>
        calculatedVariableActions.getCalculatedVariablesForStudy({
          studyId: action.payload.routerState.params.id,
        })
      )
    )
  );

  getCalculatedVariablesForStudy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(calculatedVariableActions.getCalculatedVariablesForStudy),
      switchMap(({ studyId }) => {
        return this.calculatedVariablesService.getCalculatedVariablesForStudy(studyId).pipe(
          map((calculatedVariables) => {
            const transformedCalculatedVariables = [];
            for (const calculatedVariable of calculatedVariables) {
              const transformedCalculatedVariable: CalculatedVariable = this.dashboardCamelCasePipe.keysToCamelCase(
                calculatedVariable
              );
              transformedCalculatedVariables.push(transformedCalculatedVariable);
            }
            return calculatedVariableActions.getCalculatedVariablesForStudySuccess({
              calculatedVariablesForStudy: transformedCalculatedVariables,
            });
          }),
          catchError((err: HttpErrorResponse) =>
            of(
              calculatedVariableActions.getCalculatedVariablesForStudyError({
                err,
              })
            )
          )
        );
      })
    )
  );

  getCalculatedVariablesError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(calculatedVariableActions.getCalculatedVariablesForStudyError),
        tap(({ err }) => this.toastrService.error('Unable to load calculated variables'))
      ),
    { dispatch: false }
  );

  createCalculatedVariable$ = createEffect(() =>
    this.actions$.pipe(
      ofType(calculatedVariableActions.createCalculatedVariable),
      switchMap(({ calculatedVariable }) => {
        const calculatedVariableSnakeCase = this.dashboardSnakeCasePipe.keysToSnakeCase(calculatedVariable);
        return this.calculatedVariablesService.createCalculatedVariable(calculatedVariableSnakeCase).pipe(
          map(
            (
              createdCalculatedVariable: CalculatedVariable & {
                original: CalculatedVariable;
              }
            ) => {
              const createdCalculatedVariableCamelCase = this.dashboardCamelCasePipe.keysToCamelCase(
                createdCalculatedVariable.original
              );
              return calculatedVariableActions.createCalculatedVariableSuccess({
                createdCalculatedVariable: createdCalculatedVariableCamelCase,
              });
            }
          ),
          catchError((err: HttpErrorResponse) => {
            return of(calculatedVariableActions.createCalculatedVariableError({ err }));
          })
        );
      })
    )
  );

  createCalculatedVariableSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(calculatedVariableActions.createCalculatedVariableSuccess),
        map(({ createdCalculatedVariable }) => {
          this.toastrService.success(`Calculated variable ${createdCalculatedVariable.uniqueName} created!`);
          this.dialog.closeAll();
        })
      ),
    { dispatch: false }
  );

  createCalculatedVariableError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(calculatedVariableActions.createCalculatedVariableError),
        map(({ err }) => {
          const message = err.status === 409 ? err.error.message : 'Unable to save calculated variable';
          this.toastrService.error(message);
        })
      ),
    { dispatch: false }
  );

  updateCalculatedVariable$ = createEffect(() =>
    this.actions$.pipe(
      ofType(calculatedVariableActions.updateCalculatedVariable),
      switchMap(({ calculatedVariable }) => {
        const calculatedVariableSnakeCase = this.dashboardSnakeCasePipe.keysToSnakeCase(calculatedVariable);
        return this.calculatedVariablesService.updateCalculatedVariable(calculatedVariableSnakeCase).pipe(
          map((updatedCalculatedVariable: any) => {
            const updatedCalculatedVariableCamelCase = this.dashboardCamelCasePipe.keysToCamelCase(
              updatedCalculatedVariable.original
            );
            return calculatedVariableActions.updateCalculatedVariableSuccess({
              updatedCalculatedVariable: updatedCalculatedVariableCamelCase,
            });
          }),
          catchError((err: HttpErrorResponse) => {
            return of(calculatedVariableActions.updateCalculatedVariableError({ err }));
          })
        );
      })
    )
  );

  updateCalculatedVariableSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(calculatedVariableActions.updateCalculatedVariableSuccess),
        map(({ updatedCalculatedVariable }) => {
          this.toastrService.success(`Calculated variable ${updatedCalculatedVariable.uniqueName} updated!`);
          this.dialog.closeAll();
        })
      ),
    { dispatch: false }
  );

  updateCalculatedVariableError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(calculatedVariableActions.updateCalculatedVariableError),
        map(({ err }) => {
          this.toastrService.error('Unable to update calculated variable');
        })
      ),
    { dispatch: false }
  );

  deleteCalculatedVariable$ = createEffect(() =>
    this.actions$.pipe(
      ofType(calculatedVariableActions.deleteCalculatedVariable),
      switchMap(({ calculatedVariable }) => {
        const calculatedVariableSnakeCase = this.dashboardSnakeCasePipe.keysToSnakeCase(calculatedVariable);
        return this.calculatedVariablesService.deleteCalculatedVariable(calculatedVariableSnakeCase).pipe(
          map((response: any) => {
            return response === 'deleted'
              ? calculatedVariableActions.deleteCalculatedVariableSuccess({
                  deletedCalculatedVariable: calculatedVariable,
                })
              : calculatedVariableActions.deleteCalculatedVariableError({ err: response });
          }),
          catchError((err: HttpErrorResponse) => {
            return of(calculatedVariableActions.deleteCalculatedVariableError({ err }));
          })
        );
      })
    )
  );

  deleteCalculatedVariableSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(calculatedVariableActions.deleteCalculatedVariableSuccess),
        tap(({ deletedCalculatedVariable }) => {
          this.toastrService.success(`Calculated variable ${deletedCalculatedVariable.uniqueName} is deleted!`);
        })
      ),
    { dispatch: false }
  );

  deleteCalculatedVariableError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(calculatedVariableActions.deleteCalculatedVariableError),
        tap((err) => {
          switch (err.err.toString()) {
            case 'used in module item':
              this.toastrService.error(
                'Unable to delete calculated variable. It is used in a module. Please, remove it from the module first and then try again.'
              );
              break;
            case 'used in factor':
              this.toastrService.error(
                'Unable to delete calculated variable. It is used in a template configuration. Please, remove it from the configuration first and then try again.'
              );
              break;
            default:
              this.toastrService.error('Unable to delete calculated variable!');
              break;
          }
        })
      ),
    { dispatch: false }
  );

  getCalculatedVariablesForModule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(calculatedVariableActions.getCalculatedVariablesForModule),
      switchMap(({ studyId, moduleId }) => {
        return this.calculatedVariablesService.getCalculatedVariablesForModule(studyId, moduleId).pipe(
          map((calculatedVariables) => {
            const transformedCalculatedVariables = [];
            for (const calculatedVariable of calculatedVariables) {
              const transformedCalculatedVariable: CalculatedVariable = this.dashboardCamelCasePipe.keysToCamelCase(
                calculatedVariable
              );
              transformedCalculatedVariables.push(transformedCalculatedVariable);
            }
            return calculatedVariableActions.getCalculatedVariablesForModuleSuccess({
              calculatedVariablesForModule: transformedCalculatedVariables,
            });
          }),
          catchError((err: HttpErrorResponse) =>
            of(
              calculatedVariableActions.getCalculatedVariablesForModuleError({
                err,
              })
            )
          )
        );
      })
    )
  );

  getCalculatedVariablesForModuleError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(calculatedVariableActions.getCalculatedVariablesForModuleError),
        tap(({ err }) => this.toastrService.error('Unable to load calculated variables for module'))
      ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private calculatedVariablesService: CalculatedVariablesService,
    private toastrService: ToastrService,
    private dashboardCamelCasePipe: DashboardCamelCasePipe,
    private dashboardSnakeCasePipe: DashboardSnakeCasePipe,
    private dialog: MatDialog
  ) {}
}
